import { extendTheme } from "@chakra-ui/react";
import { mode } from "@chakra-ui/theme-tools";

export const theme = extendTheme({
  fonts: {
    heading: "americane-condensed, sans-serif",
    body: "roboto, sans-serif",
    american: "americane-condensed",
    wisdom: "Wisdom Script AJ",
    myriad: "MyriadPro Regular",
    italic: "Source Sans Variable Italic",
    brush: "above-the-sky-script",
    titling: "Titling Normal"
  },
  breakpoints: {
    'xxl': '1400px',
    'md-lg': '992px', // Between md (768px) and lg (992px)
    'sm-md': '768px', // Between sm (576px) and md (768px)
  },
  shadows: {
    outline: 'none',
  },
  styles: {
    global: (props) => ({
      "*, *::before, ::after": {
        wordWrap: "normal"
      },
      body: {
        color: mode("gray.700", "whiteAlpha.900")(props),
        bg: mode("#fff", "#222")(props),
        fontSize: "1.2em",
        ".deleted": {
          color: "#ff8383 !important",
          fontStyle: "normal !important",
        },
        ".inserted": {
          color: "#b5f4a5 !important",
          fontStyle: "normal !important",
        },
        overflowX: "hidden"
      },
      a: {
        color: mode("black", "black")(props),
        transition: "color 0.15s",
        transitionTimingFunction: "ease-out",
        fontWeight: "600",
        _hover: {
          color: mode("#00262A", "#00262A")(props),
        },
      },
      p: {
        marginBottom: "0.8em"
      },
      ul: {
        marginLeft: "2em",
        marginBottom: "2em"
      },
      h2: {
        fontSize: { base: '2xl', md: '5xl' },
      },
      h3: {
        fontFamily: "Roboto",
        fontSize: { base: '2xl', md: '4xl' },
      },
      ".content-block": {
        fontFamily: "Roboto",
        fontSize: "16px",
        textAlign: "left",
        p: {
          fontFamily: "Roboto",
          fontSize: "16px",
        },
        strong: {
          fontWeight: "bold",
        },
        h1: {
          fontFamily: "americane-condensed",
          fontWeight: "800",
          fontSize: { base: '56px', md: '103px' },
          lineHeight: "1",
          color: "rgb(213, 31, 12)",
          "sub": {
            fontFamily: "above-the-sky-script",
            fontSize: { base: '208px', md: '215px' },
            color: "#ffffff"
          }
        },
        h2: {
          fontFamily: "americane-condensed",
          fontWeight: "800",
          color: "rgb(213, 31, 12)",
          lineHeight: "1",
          fontSize: { base: '44px', md: '70px' },
          "sub": {
            fontFamily: "above-the-sky-script",
            fontSize: { base: '128px', md: '135px' },
            color: "#143B49"
          }
        },
        h3: {
          fontFamily: "americane-condensed",
          fontWeight: "800",
          color: "rgb(213, 31, 12)",
          fontSize: "40px",
          margin: "10px 0 10px 0",
          lineHeight: "1",
          "sub": {
            fontFamily: "above-the-sky-script",
            fontSize: { base: '64px', md: '71px' },
            color: "#143B49"
          }
        },
        h4: {
          fontFamily: "americane-condensed",
          color: "#222",
          fontSize: { base: '30px', md: '37px' },
          lineHeight: "1.1",
          "sub": {
            fontFamily: "above-the-sky-script",
            fontSize: { base: '47px', md: '54px' },
            color: "#143B49"
          }
        },
        h5: {
          fontFamily: "Roboto",
          fontWeight: "600",
          color: "rgb(20, 59, 73)",
          lineHeight: "1.2",
          margin: "20px 0 5px 0",
          fontSize: { base: '20px', md: '27px' },
        },
        h6: {
          fontFamily: "Roboto",
          fontWeight: "600",
          color: "#143b49",
          lineHeight: "1.2",
          fontSize: { base: '15px', md: '21px' },
        },
        blockquote: {
          color: "rgb(213, 31, 12)",
          padding: "5rem 5vw",
          fontSize: { base: '22px', md: '32px' },
          lineHeight: "1.2",
          fontFamily: "italic",
          marginTop: "10px",
          marginBottom: "10px",
          p: {
            fontSize: { base: '22px', md: '32px' },
            lineHeight: "1.2",
            marginBottom: "10px",
            fontFamily: "italic"
          }
        },
        img: {
          width: "100%",
          height: "auto",
        },
        em: {
          fontFamily: "Roboto",
          fontStyle: "italic",
          fontWeight: "600",
        },
        hr: {
          mt: "5px",
          borderTop: "2px solid #16315A",
        },
        a: {
          color: "rgb(213, 31, 12)",
          _hover: {
            color: "#222",
          },
        }
      }
    }),
  },
  components: {
    Heading: {
      baseStyle: (props) => ({
        border: "none",
        pb: 2,
        fontWeight: "600",
        a: {
          color: mode("blue", "blue.200")(props),
          _hover: {
            color: mode("blue.600", "blue.300")(props),
          },
        },
      }),
    },
    Text: {
      baseStyle: {
        marginBottom: "0",
      },
    },
    Popover: {
      parts: ['popper'],
      baseStyle: (props) => ({
        popper: {
          zIndex: 10,
          maxW: 'xs',
          // maxW: props.width ? props.width : 'xs',
          w: props.width,
        },
      }),
    },
    Button: {
      baseStyle: {
        fontWeight: "bolder",
        textTransform: 'uppercase',
        fontFamily: 'Roboto',
        fontSize: '13px',
        letterSpacing: '1px',
        padding: '12px 24px',
        lineHeight: '1em',
      },
      sizes: {
        sm: {
          fontSize: { base: '10px', md: '12px' },
          px: 4,
          py: 4,
        },
        md: {
          fontSize: { base: '11px', md: '13px' },
          px: 5,
          pt: 3,
          pb: 3,
        },
      },
      variants: {
        outline: {
          bg: 'white',
          color: '#D51F0C',
          boxShadow: '1px 2px #D51F0C',
          _hover: {
            bg: "white",
            color: '#D51F0C',
          },
        },
        solid: {
          bg: '#D51F0C',
          color: 'white',
          _hover: {
            bg: "#143b49",
            color: 'white',
          },
        },
      },
      defaultProps: {
        size: 'md',
        variant: 'solid',
      },
    },
    Tag: {
      baseStyle: {
        rounded: "lg",
      },
    },
    textarea: {
      baseStyle: {
        background: "green.300",
      },
    },
    Link: {
      baseStyle: {
        fontWeight: "inherit",
        _hover: {
          textDecoration: "none",
        },
      },
      variants: {
        text: {
          color: "#222",
          transition: "color 0.15s",
          transitionTimingFunction: "ease-out",
          fontWeight: "600",
          _hover: {
            color: "blue.300",
          },
        },
        gradient: {
          bgGradient: "linear(to-br, blue.400,blue.300)",
          bgClip: "text",
          fontWeight: "600",
          _hover: {
            bgGradient: "linear(to-br, blue.500,blue.300)",
            bgClip: "text",
          },
        },
      },
    }
  },
  mdx: {

  },
});

export const getTagColor = type => {
  type = type.toLowerCase()
  if (type === "rails" || type === "ruby") {
    return "red";
  } else if (type === "react") {
    return "cyan";
  } else if (type === "javascript") {
    return "yellow";
  } else if (type === "typescript" || type === "tailwindcss") {
    return "blue";
  } else if (type === "chakraui" || type === "css") {
    return "teal";
  }
};